import React, { useEffect, useState } from 'react';
import GmailOAuthService from '../services/gmail/GmailOAuthService';
import { validateOAuthState, getOAuthState } from '../utils/oauth/oauthUtils';

/**
 * Get the return URL from localStorage where user initiated OAuth
 * Falls back to '/Profile' if not found (most common initiation point)
 */
const getReturnUrl = () => {
  const storedReturnUrl = localStorage.getItem('gmail_oauth_return_url');
  if (storedReturnUrl) {
    // Clean up the stored return URL
    localStorage.removeItem('gmail_oauth_return_url');
    return storedReturnUrl;
  }
  // Default fallback to Profile page where Gmail connection is commonly initiated
  return '/Profile';
};

/**
 * OAuth Callback Component - Enhanced with complete token exchange
 * Handles OAuth redirects and communicates with parent window
 */
const OAuthCallback = () => {
  const [status, setStatus] = useState('Processing authentication...');
  const [isComplete, setIsComplete] = useState(false);
  
  // Add error boundary for debugging
  try {
    console.log('🔍 OAuth Callback: Component constructor called');
  } catch (error) {
    console.error('🔍 OAuth Callback: Error in component constructor:', error);
  }

  useEffect(() => {
    const debugPrefix = '🔍 OAuth Callback Debug:';
    
    // IMMEDIATE FLAG: Set localStorage flag as soon as component mounts
    try {
      localStorage.setItem('oauth_callback_reached', 'true');
      localStorage.setItem('oauth_callback_timestamp', Date.now().toString());
      localStorage.setItem('oauth_callback_context', window.opener ? 'popup' : 'direct');
      console.log('🔍 OAuth Callback: Component mounted and reached successfully');
      console.log('🔍 OAuth Callback: localStorage flag set - oauth_callback_reached = true');
      console.log('🔍 OAuth Callback: Context type:', window.opener ? 'popup' : 'direct');
    } catch (storageError) {
      console.error('🔍 OAuth Callback: Failed to set localStorage flags:', storageError);
    }
    
    console.log(`${debugPrefix} OAuth callback component loaded`);
    console.log(`${debugPrefix} Current URL:`, window.location.href);
    console.log(`${debugPrefix} URL params:`, new URLSearchParams(window.location.search));
    console.log(`${debugPrefix} Window opener exists:`, !!window.opener);
    console.log(`${debugPrefix} LocalStorage available:`, typeof(Storage) !== "undefined");
    console.log(`${debugPrefix} Document title:`, document.title);
    console.log(`${debugPrefix} Document ready state:`, document.readyState);
    console.log(`${debugPrefix} Window location origin:`, window.location.origin);
    console.log(`${debugPrefix} Component mounted successfully - no auth redirect occurred`);
    
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    console.log(`${debugPrefix} OAuth parameters:`, { code, state, error, errorDescription });
    
    // Check if we're in a popup
    if (window.opener) {
      console.log(`${debugPrefix} Running in popup, communicating with parent window`);
      setStatus('Processing OAuth response...');
      
      const processCallback = async () => {
        try {
          if (error) {
            // Handle OAuth error
            console.error(`${debugPrefix} OAuth error:`, error, errorDescription);
            const errorData = {
              success: false,
              error: errorDescription || error,
              timestamp: Date.now(),
              source: 'oauth_callback_error',
              rawError: error
            };
            
            setStatus('Authentication failed');
            localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
            console.log(`${debugPrefix} Error stored in localStorage:`, errorData);
          } else if (code) {
            // OAuth success - validate state and exchange code for tokens
            console.log(`${debugPrefix} OAuth success, validating state and exchanging code for tokens...`);
            setStatus('Validating authentication...');
            
            // Validate OAuth state
            const storedState = getOAuthState();
            console.log(`${debugPrefix} Stored OAuth state:`, storedState);
            console.log(`${debugPrefix} Received OAuth state:`, state);
            
            if (!validateOAuthState(state, storedState)) {
              console.error(`${debugPrefix} OAuth state validation failed`);
              const errorData = {
                success: false,
                error: 'Invalid OAuth state. Please try again.',
                timestamp: Date.now(),
                source: 'state_validation_failed'
              };
              
              setStatus('Authentication failed - invalid state');
              localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
              console.log(`${debugPrefix} State validation error stored in localStorage:`, errorData);
            } else {
              console.log(`${debugPrefix} OAuth state validation passed, exchanging code for tokens...`);
              setStatus('Exchanging authorization code for tokens...');
              
              try {
                // Exchange authorization code for tokens
                const tokens = await GmailOAuthService.getTokens(code);
                console.log(`${debugPrefix} Token exchange successful:`, {
                  hasAccessToken: !!tokens.access_token,
                  hasRefreshToken: !!tokens.refresh_token,
                  expiresIn: tokens.expires_in
                });
                
                setStatus('Getting user profile...');
                
                // Get user profile
                const profile = await GmailOAuthService.getUserProfile(tokens);
                console.log(`${debugPrefix} Profile retrieved:`, {
                  email: profile.email,
                  name: profile.name,
                  id: profile.id,
                  verified: profile.verified_email
                });
                
                // Prepare complete OAuth data
                const oauthData = {
                  access_token: tokens.access_token,
                  refresh_token: tokens.refresh_token,
                  email: profile.email,
                  profile: {
                    id: profile.id,
                    name: profile.name,
                    picture: profile.picture,
                    verified_email: profile.verified_email
                  },
                  expires_in: tokens.expires_in,
                  token_type: tokens.token_type,
                  scope: tokens.scope
                };
                
                const successData = {
                  success: true,
                  data: oauthData,
                  timestamp: Date.now(),
                  source: 'oauth_callback_success'
                };
                
                console.log(`${debugPrefix} Complete OAuth data prepared:`, oauthData);
                setStatus('Authentication successful!');
                
                // Store in localStorage with explicit logging
                try {
                  localStorage.setItem('gmail_oauth_result', JSON.stringify(successData));
                  console.log(`${debugPrefix} ✅ Success data stored in localStorage:`, successData);
                  
                  // Verify it was stored
                  const verify = localStorage.getItem('gmail_oauth_result');
                  if (verify) {
                    console.log(`${debugPrefix} ✅ Verified: Data is in localStorage`);
                  } else {
                    console.error(`${debugPrefix} ❌ ERROR: Data was NOT stored in localStorage!`);
                  }
                } catch (storageError) {
                  console.error(`${debugPrefix} ❌ ERROR storing in localStorage:`, storageError);
                }
                
              } catch (tokenError) {
                console.error(`${debugPrefix} Token exchange failed:`, tokenError);
                const errorData = {
                  success: false,
                  error: `Token exchange failed: ${tokenError.message}`,
                  timestamp: Date.now(),
                  source: 'token_exchange_failed',
                  details: tokenError.message
                };
                
                setStatus('Token exchange failed');
                localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
                console.log(`${debugPrefix} Token exchange error stored in localStorage:`, errorData);
              }
            }
          } else {
            // No code or error - likely user cancelled
            console.log(`${debugPrefix} No OAuth code found, user may have cancelled`);
            const cancelData = {
              success: false,
              error: 'Authentication was cancelled. Please try again.',
              timestamp: Date.now(),
              source: 'user_cancelled'
            };
            
            setStatus('Authentication cancelled');
            localStorage.setItem('gmail_oauth_result', JSON.stringify(cancelData));
            console.log(`${debugPrefix} Cancellation stored in localStorage:`, cancelData);
          }
          
          // Mark as complete and close popup
          setIsComplete(true);
          setStatus('Closing window...');
          
          // Wait a moment to ensure localStorage is set, then close the popup
          setTimeout(() => {
            console.log(`${debugPrefix} Closing popup window`);
            window.close();
          }, 1000);
          
        } catch (error) {
          console.error(`${debugPrefix} Unexpected error in callback processing:`, error);
          const errorData = {
            success: false,
            error: `Unexpected error: ${error.message}`,
            timestamp: Date.now(),
            source: 'unexpected_error',
            details: error.message
          };
          
          setStatus('Unexpected error occurred');
          localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
          console.log(`${debugPrefix} Unexpected error stored in localStorage:`, errorData);
          
          setIsComplete(true);
          setTimeout(() => {
            window.close();
          }, 1000);
        }
      };
      
      // Process the callback
      processCallback();
      
    } else {
      // Not in popup - process OAuth callback in same window
      console.log(`${debugPrefix} Not in popup, processing OAuth in same window`);
      setStatus('Processing OAuth response...');
      
      const processCallback = async () => {
        try {
          if (error) {
            // Handle OAuth error
            console.error(`${debugPrefix} OAuth error:`, error, errorDescription);
            const errorData = {
              success: false,
              error: errorDescription || error,
              timestamp: Date.now(),
              source: 'oauth_callback_error',
              rawError: error
            };
            
            setStatus('Authentication failed');
            localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
            console.log(`${debugPrefix} Error stored in localStorage:`, errorData);
            
            // Redirect after storing error
            setIsComplete(true);
            setTimeout(() => {
              const returnUrl = getReturnUrl();
              console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
              window.location.href = returnUrl;
            }, 2000);
          } else if (code) {
            // OAuth success - validate state and exchange code for tokens
            console.log(`${debugPrefix} OAuth success, validating state and exchanging code for tokens...`);
            setStatus('Validating authentication...');
            
            // Validate OAuth state
            const storedState = getOAuthState();
            console.log(`${debugPrefix} Stored OAuth state:`, storedState);
            console.log(`${debugPrefix} Received OAuth state:`, state);
            
            if (!validateOAuthState(state, storedState)) {
              console.error(`${debugPrefix} OAuth state validation failed`);
              const errorData = {
                success: false,
                error: 'Invalid OAuth state. Please try again.',
                timestamp: Date.now(),
                source: 'state_validation_failed'
              };
              
              setStatus('Authentication failed - invalid state');
              localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
              console.log(`${debugPrefix} State validation error stored in localStorage:`, errorData);
              
              setIsComplete(true);
              setTimeout(() => {
                const returnUrl = getReturnUrl();
                console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
                window.location.href = returnUrl;
              }, 2000);
            } else {
              console.log(`${debugPrefix} OAuth state validation passed, exchanging code for tokens...`);
              setStatus('Exchanging authorization code for tokens...');
              
              try {
                // Exchange authorization code for tokens
                const tokens = await GmailOAuthService.getTokens(code);
                console.log(`${debugPrefix} Token exchange successful:`, {
                  hasAccessToken: !!tokens.access_token,
                  hasRefreshToken: !!tokens.refresh_token,
                  expiresIn: tokens.expires_in
                });
                
                setStatus('Getting user profile...');
                
                // Get user profile
                const profile = await GmailOAuthService.getUserProfile(tokens);
                console.log(`${debugPrefix} Profile retrieved:`, {
                  email: profile.email,
                  name: profile.name,
                  id: profile.id,
                  verified: profile.verified_email
                });
                
                // Prepare complete OAuth data
                const oauthData = {
                  access_token: tokens.access_token,
                  refresh_token: tokens.refresh_token,
                  email: profile.email,
                  profile: {
                    id: profile.id,
                    name: profile.name,
                    picture: profile.picture,
                    verified_email: profile.verified_email
                  },
                  expires_in: tokens.expires_in,
                  token_type: tokens.token_type,
                  scope: tokens.scope
                };
                
                const successData = {
                  success: true,
                  data: oauthData,
                  timestamp: Date.now(),
                  source: 'oauth_callback_success'
                };
                
                console.log(`${debugPrefix} Complete OAuth data prepared:`, oauthData);
                setStatus('Authentication successful!');
                
                // Store in localStorage with explicit logging
                try {
                  localStorage.setItem('gmail_oauth_result', JSON.stringify(successData));
                  console.log(`${debugPrefix} ✅ Success data stored in localStorage:`, successData);
                  
                  // Verify it was stored
                  const verify = localStorage.getItem('gmail_oauth_result');
                  if (verify) {
                    console.log(`${debugPrefix} ✅ Verified: Data is in localStorage`);
                  } else {
                    console.error(`${debugPrefix} ❌ ERROR: Data was NOT stored in localStorage!`);
                  }
                } catch (storageError) {
                  console.error(`${debugPrefix} ❌ ERROR storing in localStorage:`, storageError);
                }
                
                // Mark as complete and redirect after storing result
                setIsComplete(true);
                setStatus('Redirecting to main application...');
                
                // Wait a moment to ensure localStorage is set, then redirect
                setTimeout(() => {
                  const returnUrl = getReturnUrl();
                  console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
                  window.location.href = returnUrl;
                }, 1500);
                
              } catch (tokenError) {
                console.error(`${debugPrefix} Token exchange failed:`, tokenError);
                const errorData = {
                  success: false,
                  error: `Token exchange failed: ${tokenError.message}`,
                  timestamp: Date.now(),
                  source: 'token_exchange_failed',
                  details: tokenError.message
                };
                
                setStatus('Token exchange failed');
                localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
                console.log(`${debugPrefix} Token exchange error stored in localStorage:`, errorData);
                
                setIsComplete(true);
                setTimeout(() => {
                  const returnUrl = getReturnUrl();
                  console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
                  window.location.href = returnUrl;
                }, 2000);
              }
            }
          } else {
            // No code or error - likely user cancelled
            console.log(`${debugPrefix} No OAuth code found, user may have cancelled`);
            const cancelData = {
              success: false,
              error: 'Authentication was cancelled. Please try again.',
              timestamp: Date.now(),
              source: 'user_cancelled'
            };
            
            setStatus('Authentication cancelled');
            localStorage.setItem('gmail_oauth_result', JSON.stringify(cancelData));
            console.log(`${debugPrefix} Cancellation stored in localStorage:`, cancelData);
            
            setIsComplete(true);
            setTimeout(() => {
              const returnUrl = getReturnUrl();
              console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
              window.location.href = returnUrl;
            }, 2000);
          }
          
        } catch (error) {
          console.error(`${debugPrefix} Unexpected error in callback processing:`, error);
          const errorData = {
            success: false,
            error: `Unexpected error: ${error.message}`,
            timestamp: Date.now(),
            source: 'unexpected_error',
            details: error.message
          };
          
          setStatus('Unexpected error occurred');
          localStorage.setItem('gmail_oauth_result', JSON.stringify(errorData));
          console.log(`${debugPrefix} Unexpected error stored in localStorage:`, errorData);
          
          setIsComplete(true);
          setTimeout(() => {
            const returnUrl = getReturnUrl();
            console.log(`${debugPrefix} Redirecting to return URL:`, returnUrl);
            window.location.href = returnUrl;
          }, 2000);
        }
      };
      
      // Process the callback
      processCallback();
    }
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <div style={{ 
        textAlign: 'center',
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '100%'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #2196f3',
          borderRadius: '50%',
          animation: isComplete ? 'none' : 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }} />
        
        <h2 style={{ 
          margin: '0 0 10px 0',
          color: isComplete ? '#4caf50' : '#333',
          fontSize: '24px'
        }}>
          {status}
        </h2>
        
        <p style={{ 
          color: '#666',
          margin: '0',
          lineHeight: '1.5'
        }}>
          {isComplete ? 
            'This window will close automatically.' : 
            'Please wait while we complete your Gmail connection.'
          }
        </p>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OAuthCallback;
